import { useState, useEffect, axios, Button, Card, CardContent, CardHeader, CardTitle, Badge, Loader2, Copy, X, Twitter, Linkedin, Facebook, MessageCircle, FullCalendar, dayGridPlugin, interactionPlugin, API, useAuth, toast } from "@/pages/shared";

export default function CalendarPage() {
  const { token } = useAuth();
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => { fetchContent(); }, [token]);

  const fetchContent = async () => {
    try {
      const res = await axios.get(`${API}/content`, { headers: { Authorization: `Bearer ${token}` } });
      setContent(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Convert UTC ISO timestamps back to the user's local date so events land on the
  // correct day in their browser timezone (a 22:00 UTC schedule for an IST user is
  // actually the next morning locally).
  const toLocalDateKey = (iso) => {
    const d = new Date(iso);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };

  const calendarEvents = content.map(c => ({
    id: c.id,
    title: c.topic,
    date: c.scheduled_at ? toLocalDateKey(c.scheduled_at) : toLocalDateKey(c.created_at),
    backgroundColor: c.status === 'scheduled' ? '#10b981' : c.content_type === 'blog' ? '#7C5CFC' : c.content_type === 'social' ? '#3b82f6' : '#f59e0b',
    borderColor: 'transparent',
    extendedProps: { ...c }
  }));

  const handleEventClick = (info) => { setSelectedEvent(info.event.extendedProps); };

  const handleEventDrop = async (info) => {
    const contentId = info.event.id;
    const newDate = info.event.startStr; // YYYY-MM-DD in user's local view
    const props = info.event.extendedProps || {};
    try {
      // Build a Date at 09:00 LOCAL time, then send as ISO (UTC) so the backend's
      // UTC scheduler fires at the right moment for this creator's region.
      const local9am = new Date(`${newDate}T09:00:00`);
      const scheduled_at = local9am.toISOString();
      // Preserve the existing auto-post-to-X flag if the user already set one;
      // otherwise default ON so a drag-to-reschedule actually publishes.
      const post_to_twitter = props.post_to_twitter !== false;
      await axios.post(
        `${API}/content/schedule`,
        { content_id: contentId, scheduled_at, post_to_twitter },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const tzLabel = Intl.DateTimeFormat().resolvedOptions().timeZone;
      toast.success(post_to_twitter ? `Will auto-post 09:00 ${tzLabel}` : `Reminder set 09:00 ${tzLabel}`);
      fetchContent();
    } catch (e) { toast.error("Failed to reschedule"); info.revert(); }
  };

  const handleDateClick = (info) => { toast.info(`Generate content for ${info.dateStr} from the Content page`); };

  const unscheduleEvent = async (contentId) => {
    const prior = content.find((c) => c.id === contentId);
    const priorScheduledAt = prior?.scheduled_at;
    const priorPostToTwitter = prior?.post_to_twitter;
    try {
      await axios.post(`${API}/content/${contentId}/unschedule`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedEvent(null);
      fetchContent();
      toast.success("Schedule cancelled", {
        action: priorScheduledAt ? {
          label: "Undo",
          onClick: async () => {
            try {
              await axios.post(
                `${API}/content/schedule`,
                { content_id: contentId, scheduled_at: priorScheduledAt, post_to_twitter: !!priorPostToTwitter },
                { headers: { Authorization: `Bearer ${token}` } }
              );
              toast.success("Schedule restored");
              fetchContent();
            } catch (e) { toast.error("Couldn't restore schedule"); }
          },
        } : undefined,
        duration: 6000,
      });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to cancel"); }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold mb-2">Content Calendar</h1>
        <p className="text-muted-foreground">Drag and drop to reschedule your content · <span className="text-cyan-400" data-testid="calendar-tz-label">Times shown in {userTz}</span></p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3">
          <Card className="glass">
            <CardContent className="p-4">
              {loading ? (
                <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin" /></div>
              ) : (
                <div className="calendar-container">
                  <FullCalendar
                    plugins={[dayGridPlugin, interactionPlugin]}
                    initialView="dayGridMonth"
                    events={calendarEvents}
                    editable={true}
                    droppable={true}
                    eventClick={handleEventClick}
                    eventDrop={handleEventDrop}
                    dateClick={handleDateClick}
                    headerToolbar={{ left: 'prev,next today', center: 'title', right: 'dayGridMonth,dayGridWeek' }}
                    height="auto"
                    eventDisplay="block"
                    dayMaxEvents={3}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="glass">
            <CardHeader><CardTitle className="text-lg">Legend</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-3"><div className="w-4 h-4 rounded bg-violet-600"></div><span className="text-sm">Blog Post</span></div>
              <div className="flex items-center gap-3"><div className="w-4 h-4 rounded bg-blue-500"></div><span className="text-sm">Social Media</span></div>
              <div className="flex items-center gap-3"><div className="w-4 h-4 rounded bg-amber-500"></div><span className="text-sm">Ad Copy</span></div>
              <div className="flex items-center gap-3"><div className="w-4 h-4 rounded bg-teal-500"></div><span className="text-sm">Scheduled</span></div>
            </CardContent>
          </Card>

          {selectedEvent && (
            <Card className="glass">
              <CardHeader>
                <CardTitle className="text-lg">Content Details</CardTitle>
                <Button variant="ghost" size="icon" className="absolute top-4 right-4" onClick={() => setSelectedEvent(null)}><X className="w-4 h-4" /></Button>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Badge variant="secondary">{selectedEvent.content_type}</Badge>
                  {selectedEvent.status === 'scheduled' && <Badge className="ml-2 bg-teal-500/20 text-teal-500">Scheduled</Badge>}
                </div>
                <p className="font-medium">{selectedEvent.topic}</p>
                {selectedEvent.scheduled_at && (
                  <p className="text-xs text-teal-400" data-testid="event-local-time">
                    Posts at {new Date(selectedEvent.scheduled_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                  </p>
                )}
                <p className="text-sm text-muted-foreground line-clamp-4">{selectedEvent.generated_content?.substring(0, 200)}...</p>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(selectedEvent.generated_content); toast.success("Copied!"); }}>
                    <Copy className="w-3 h-3 mr-1" /> Copy
                  </Button>
                  {selectedEvent.status === 'scheduled' && !selectedEvent.twitter_posted && (
                    <Button size="sm" variant="outline" className="text-amber-400 border-amber-400/30 hover:bg-amber-400/10" onClick={() => unscheduleEvent(selectedEvent.id)} data-testid="calendar-unschedule-btn">
                      <X className="w-3 h-3 mr-1" /> Cancel schedule
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="text-blue-400 border-blue-400/30 hover:bg-blue-400/10" onClick={() => {
                    let tweetText = selectedEvent.generated_content;
                    if (tweetText.length > 275) tweetText = tweetText.substring(0, 272) + "...";
                    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`, '_blank');
                  }} data-testid="calendar-share-twitter-btn">
                    <Twitter className="w-3 h-3 mr-1" /> X
                  </Button>
                  <Button size="sm" variant="outline" className="text-blue-600 border-blue-600/30 hover:bg-blue-600/10" onClick={() => {
                    const text = selectedEvent.generated_content.length > 700 ? selectedEvent.generated_content.substring(0, 697) + "..." : selectedEvent.generated_content;
                    window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(window.location.origin)}&title=${encodeURIComponent(selectedEvent.topic)}&summary=${encodeURIComponent(text)}`, '_blank');
                  }}>
                    <Linkedin className="w-3 h-3 mr-1" /> LinkedIn
                  </Button>
                  <Button size="sm" variant="outline" className="text-blue-500 border-blue-500/30 hover:bg-blue-500/10" onClick={() => {
                    const text = selectedEvent.generated_content.length > 500 ? selectedEvent.generated_content.substring(0, 497) + "..." : selectedEvent.generated_content;
                    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.origin)}&quote=${encodeURIComponent(text)}`, '_blank');
                  }}>
                    <Facebook className="w-3 h-3 mr-1" /> Facebook
                  </Button>
                  <Button size="sm" variant="outline" className="text-green-500 border-green-500/30 hover:bg-green-500/10" onClick={() => {
                    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(selectedEvent.generated_content)}`, '_blank');
                  }}>
                    <MessageCircle className="w-3 h-3 mr-1" /> WhatsApp
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="glass">
            <CardHeader><CardTitle className="text-lg">Stats</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between"><span className="text-muted-foreground">Total Content</span><span className="font-bold">{content.length}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Scheduled</span><span className="font-bold text-teal-500">{content.filter(c => c.status === 'scheduled').length}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">This Month</span><span className="font-bold">{content.filter(c => new Date(c.created_at).getMonth() === new Date().getMonth()).length}</span></div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
